import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import AccountCircle from '@mui/icons-material/AccountCircle'
import LogoutIcon from '@mui/icons-material/Logout'
import PhoneIcon from '@mui/icons-material/Phone'
import BusinessIcon from '@mui/icons-material/Business'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { useSnackbar } from 'notistack'

import { useUserContext } from '../contexts/UserContext'
import {
  Avatar,
  Button,
  Divider,
  Drawer,
  Switch,
  Typography,
} from '@mui/material'
import * as AuthUtils from '../utils/auth'
import { RootRoutes } from '../utils/routeGenerator'
import { disablePush, setPushOptOut } from '../utils/push'
import { usePushNotifications } from '../hooks/usePushNotifications'
import { SnackbarVariant } from '../constants/properties'

import { logout } from '../services/auth'

export default function PrimarySearchAppBar({ setOpen }: { setOpen: any }) {
  const [profileOpen, setProfileOpen] = React.useState(false)

  const { user, company } = useUserContext()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const push = usePushNotifications()

  const handleLogout = async () => {
    setProfileOpen(false)
    // Stop notifications for this device before auth is gone
    await disablePush().catch(() => null)
    const refreshToken = AuthUtils.getRefreshToken() || ''
    AuthUtils.removeAuthCookie()
    await logout({ refreshToken })
    AuthUtils.removeRefreshToken()
    navigate(RootRoutes.login())
  }

  const handlePushToggle = async () => {
    try {
      if (push.status === 'enabled') {
        await push.disable()
        // Explicit off — remember it so login auto-restore respects the choice
        if (user?.userId) setPushOptOut(user.userId, true)
        enqueueSnackbar('Push notifications turned off', {
          variant: SnackbarVariant.INFO,
        })
      } else {
        await push.enable()
        if (user?.userId) setPushOptOut(user.userId, false)
        enqueueSnackbar('Push notifications enabled on this device', {
          variant: SnackbarVariant.SUCCESS,
        })
      }
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Could not update push notifications', {
        variant: SnackbarVariant.ERROR,
      })
    }
  }

  const pushHelperText = {
    loading: 'Checking…',
    unsupported: 'Not supported on this device',
    blocked: 'Blocked in browser settings',
    enabled: 'Alerts for checkouts, maintenance and vehicle events',
    disabled: 'Alerts for checkouts, maintenance and vehicle events',
  }[push.status]

  const initials = user
    ? `${(user.firstName || '')[0] || ''}${
        (user.lastName || '')[0] || ''
      }`.toUpperCase()
    : ''

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Hi, {user?.firstName}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            size="large"
            edge="end"
            aria-label="profile"
            onClick={() => setProfileOpen(true)}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Bottom Sheet */}
      <Drawer
        anchor="bottom"
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '60vh',
            paddingBottom: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Handle bar */}
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: 'grey.300',
              mx: 'auto',
              mb: 3,
            }}
          />

          {/* Profile header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'primary.main',
                fontSize: '1.2rem',
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {user?.mobile}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Company info */}
          {company && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                mb: 3,
              }}
            >
              <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {(company as any)?.companyName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(company as any)?.status}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Push notifications */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <NotificationsIcon
                sx={{ color: 'text.secondary', fontSize: 20 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Push notifications
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {pushHelperText}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={push.status === 'enabled'}
              onChange={handlePushToggle}
              disabled={
                push.busy ||
                push.status === 'loading' ||
                push.status === 'unsupported' ||
                push.status === 'blocked'
              }
              inputProps={{ 'aria-label': 'Push notifications' }}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Logout */}
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Logout
          </Button>
        </Box>
      </Drawer>
    </Box>
  )
}
